import {Alert, Collapse, Divider, message, Modal, Table, Tooltip} from "antd";
import Maybe from "graphql/tsutils/Maybe";
import React, {FC, useEffect, useMemo, useState} from "react";
import {ColumnProps} from "antd/lib/table";
import {ExclamationCircleOutlined} from "@ant-design/icons";
import {red} from "@ant-design/colors";
import {
    GetAppSettingsQuery,
    PaymentMethod,
    PaymentProvider,
    ScheduledPayment,
    useGetAppSettingsQuery,
    useGetPaymentMethodsByBookerIdQuery,
    useChangeDefaultPaymentMethodMutation,
    GetPaymentMethodsByBookerIdDocument,
    GetBookingByIdDocument,
    GetAttendeeInfosForBookingQuery,
    GetBookingByIdWithCourseLessonBookingsDocument,
} from "../../generated/graphql";
import PaymentMethodBadge from "../PaymentMethodBadge/PaymentMethodBadge";
import BookingAddPaymentMethod from "../BookingAddPaymentMethod/BookingAddPaymentMethod";
// Phase 23 / Plan 23-07 — Pseudo-PaymentMethod-Entry for Mollie-default.
import {AUTO_CREATE_ONLINE_PAYMENT_METHOD_MARKER} from "../../helpers/mollieAutoCreatePaymentMethodMarker";
import css from "./PaymentMethodTable.less";
import {getPaymentMethodType, PaymentMethodData} from "../../helpers/getPaymentMethodType";
import {isPaymentMethodUsableForBooking} from "../../helpers/isPaymentMethodUsableForBooking";
import {getProviderForPaymentMethodType} from "../../helpers/paymentMethodProvider";
import {pickFirstEnabledOwnMethod} from "../../helpers/resolveDefaultPaymentMethod";
import {useStaffPaymentMethodRestriction} from "../../hooks/useStaffPaymentMethodRestriction";
import i18n from "../../services/i18n";
import {BookingAttendee} from "../../containers/bookings/CreateEditBookings/BookingCreate";

/**
 * Zeilen-Typ der Tabelle: die vom Klassifikator gelesenen Felder plus die ID.
 * Die ID braucht die Mitarbeiter-Sperre (ADR 0013) für die Bestandsausnahme.
 */
type PaymentMethodRow = PaymentMethodData & {id?: string};

type PossibleTranslations = {
    sepaDebit: "Lastschrift";
    esr: "Einzahlungsschein";
    creditCard: "Kreditkarte";
    payPal: "PayPal";
    cash: "Bar";
    bankTransfer: "Überweisung";
    onlinePayment: "Online-Zahlung";
    none: "Keine";
};

type PaymentMethodTableProps = {
    paymentMethods: Array<Partial<PaymentMethod>>;
    paymentMethodId: Maybe<string>;
    setSelectedPayment: Function;
    showHeader?: boolean;
    bookerId?: string;
    interactive?: boolean;
    attendee?: BookingAttendee | GetAttendeeInfosForBookingQuery["getAttendeeInfosForBooking"];
    bookingId?: string;
    // Mitarbeiter-Sperre (ADR 0013), Bestandsausnahme: die GESPEICHERTE Methode der
    // bearbeiteten Buchung. Sie bleibt wählbar, auch wenn ihre Art gesperrt ist.
    // Beim Umwandeln einer Platzreservierung nicht setzen — deren Methode ist nur
    // der vom Server eingetragene Teilnehmer-Standard, kein Bestand.
    storedPaymentMethodId?: Maybe<string>;
    // Phase 12 / LIFE-03+04: SPs der aktuellen Buchung — erlaubt Read-Only-Check (D-10),
    // Warn-Modal-Trigger bei offener Mollie-SP (D-11) sowie Info-Modal bei
    // Attendee-Default-Change (D-04, B2). `paymentMethodId` ist optional, weil
    // die Caller-Query-Shapes unterschiedlich sind (PanelCardPaymentData liefert
    // es nicht; Logik verlangt es nicht — nur paymentDone + paymentProvider sind
    // Entscheidungs-Input).
    scheduledPayments?: Array<
        Pick<ScheduledPayment, "id" | "paymentDone" | "paymentProvider"> & {
            paymentMethodId?: ScheduledPayment["paymentMethodId"];
        }
    >;
    // Phase 3 (P3-3) — Mollie-Modus, dreiwertig (ersetzt den früheren Boolean,
    // Adversarial R8). Spiegelt die drei Kurs-Zustände:
    //   - "off"       ⇒ Klassik: keine Online-Zeile, Caller setzt Default. Unverändert.
    //   - "mandatory" ⇒ Nur-Online: Online-Pseudo-Eintrag wird injiziert und
    //                   beim Mount FORCIERT vorausgewählt (überschreibt den
    //                   Bucher-Default). Heutiges Verhalten.
    //   - "optional"  ⇒ Kombi: Online-Pseudo-Eintrag wird ZUSÄTZLICH zu den eigenen
    //                   Methoden injiziert. Ein gültiger (vom Caller gesetzter)
    //                   Default wird NICHT überschrieben; nur wenn keine gültige
    //                   Vorauswahl existiert, wird Online vorausgewählt.
    // Backend resolved den Sentinel `AUTO_CREATE_ONLINE_PAYMENT_METHOD_MARKER`
    // beim createBooking. Admin kann nach Mount jede Zeile aktiv wählen.
    mollieMode?: MollieTableMode;
    // Kompaktdarstellung: nur die für diese Buchung aktive Methode wird gezeigt.
    // Alle anderen Methoden des Buchers sowie noch nicht angelegte Methoden der
    // Organisation liegen eingeklappt unter „Zahlmethode ändern“.
    activeOnly?: boolean;
    // Kurs-ID für das Anlegen neuer Bezahlmethoden (nur mit activeOnly relevant).
    courseId?: string;
    // Meldet dem Aufrufer, ob der Bereich „Zahlmethode ändern“ offen ist (z. B. um
    // einen umgebenden Modal-Footer solange auszublenden).
    onChangeSectionToggle?: (open: boolean) => void;
};

/** Dreiwertiger Mollie-Modus der PaymentMethodTable (Phase 3, P3-3). */
export type MollieTableMode = "off" | "mandatory" | "optional";

const PaymentMethodTable: FC<PaymentMethodTableProps> = ({
    paymentMethods,
    paymentMethodId,
    setSelectedPayment,
    showHeader = true,
    bookerId,
    interactive = true,
    attendee,
    bookingId,
    storedPaymentMethodId,
    scheduledPayments,
    mollieMode = "off",
    activeOnly = false,
    courseId,
    onChangeSectionToggle,
}) => {
    // Abgeleitete Modus-Flags (dreiwertig). `mollieEnabled` = die Tabelle zeigt
    // eine Online-Zeile (mandatory ODER optional).
    const isMollieMandatory = mollieMode === "mandatory";
    const isMollieOptional = mollieMode === "optional";
    const mollieEnabled = isMollieMandatory || isMollieOptional;
    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<string> | Array<number>>([]);
    const [changeSectionOpen, setChangeSectionOpenState] = useState(false);
    const setChangeSectionOpen = (open: boolean) => {
        setChangeSectionOpenState(open);
        if (onChangeSectionToggle) onChangeSectionToggle(open);
    };

    // Phase 12 / LIFE-03 (D-10): "Gesperrt" = in der Buchung existiert eine bezahlte
    // Mollie-SP. Dann dürfen non-Online-Radios NICHT auswählbar sein (alle non-Online-
    // Zeilen werden disabled — strengere Variante aus Plan 12-02 Task 1d).
    // Explicit === null && === undefined-Check wegen Pitfall 1 (TypeORM-Hydration).
    const hasLockedMollieSPInBooking = useMemo(() => {
        if (!scheduledPayments || scheduledPayments.length === 0) return false;
        return scheduledPayments.some(
            (sp) =>
                sp.paymentDone !== null &&
                sp.paymentDone !== undefined &&
                sp.paymentProvider === PaymentProvider.Mollie,
        );
    }, [scheduledPayments]);

    // Phase 12 / LIFE-04 (D-11): Warn-Modal-Trigger — offene (unbezahlte) Mollie-SP
    // existiert in der Buchung. Wechsel auf Non-Online-Methode invalidiert den Link.
    const hasOpenMollieSPInBooking = useMemo(() => {
        if (!scheduledPayments || scheduledPayments.length === 0) return false;
        return scheduledPayments.some(
            (sp) =>
                (sp.paymentDone === null || sp.paymentDone === undefined) &&
                sp.paymentProvider === PaymentProvider.Mollie,
        );
    }, [scheduledPayments]);

    // MITARBEITER-SPERRE (ADR 0013, Issue #1006) ------------------------------------------------------------------
    // Ohne den Tenant-Schalter `restrictStaffPaymentMethods` liefert der Hook für
    // alles `true` ⇒ exakt heutiges Verhalten (nur rotes Warn-Icon).
    const staffRestriction = useStaffPaymentMethodRestriction();

    // Bestandsausnahme: Die gespeicherte Methode einer bestehenden Buchung
    // (`storedPaymentMethodId`) bleibt wählbar und vorausgewählt — sonst könnte der
    // Mitarbeiter eine Altbuchung nicht mehr speichern, ohne die Bezahlart zu
    // wechseln. Bewusst die gespeicherte und nicht die aktuell gewählte Methode:
    // Wer wegwechselt, kann zurückwechseln — die Rückwahl gilt nur für die Buchung,
    // nie für den Teilnehmer-Standard (#1033). Aufrufer müssen daher die Methode aus
    // der DB übergeben, nicht den Formularwert. Im Anlage- und Umwandlungs-Fluss gibt
    // es keine Ausnahme (der Teilnehmer-Standard ist kein Bestand).
    /** Art der Methode ist beim Tenant deaktiviert UND der Schalter ist an. */
    const isStaffRestrictedType = (record: PaymentMethodRow) => !staffRestriction.isSelectable(record);

    /** Wie oben, aber die Bestandsmethode der Buchung bleibt ausgenommen. */
    const isStaffBlocked = (record: PaymentMethodRow) =>
        isStaffRestrictedType(record)
        && (storedPaymentMethodId === null || storedPaymentMethodId === undefined || record.id !== storedPaymentMethodId);

    // GET PAYMENTMETHODS OF COMPANY -----------------------------------------------------------------------------
    type CompanySettings = GetAppSettingsQuery["settings"];
    type CompanyMethods = Pick<CompanySettings, "cash" | "esr" | "bankTransfer" | "sepaDebit">;

    const [companySettings, setCompanySettings] = useState<CompanySettings>();
    const [companyPaymentMethods, setCompanyPaymentMethods] = useState<CompanyMethods>();

    const {data: companySettingsData, loading: loadingSettings} = useGetAppSettingsQuery();

    useEffect(() => {
        if (companySettingsData) {
            setCompanySettings(companySettingsData.settings);

            const companyPamentMethods = {
                cash: companySettingsData.settings.cash,
                esr: companySettingsData.settings.esr,
                bankTransfer: companySettingsData.settings.bankTransfer,
                sepaDebit: companySettingsData.settings.sepaDebit,
            };

            setCompanyPaymentMethods(companyPamentMethods);
        }
    }, [companySettingsData]);

    // GET PAYMENTMETHODS OF BOOKER ------------------------------------------------------------------------------
    const [paymentMethodsOfBooker, setPaymentMethodsOfBooker] = useState<Array<Partial<PaymentMethod>>>(paymentMethods);
    const {data: bookerPaymentMethodsData} = useGetPaymentMethodsByBookerIdQuery({
        skip: !bookerId,
        variables: {bookerId: bookerId ?? ""},
        fetchPolicy: "network-only",
    });

    // console.debug("paymentMethodsOfBooker", paymentMethodsOfBooker);
    const defaultPaymentMethodId = attendee?.defaultPaymentMethod?.id;

    const refetchQueries = () => {
        const queries = [];

        if (bookingId) {
            queries.push({
                query: GetBookingByIdDocument,
                variables: {id: bookingId},
            });
            queries.push({
                query: GetBookingByIdWithCourseLessonBookingsDocument,
                variables: {id: bookingId},
            });
        }

        if (bookerId) {
            queries.push({
                query: GetPaymentMethodsByBookerIdDocument,
                variables: {bookerId},
            });
        }

        return queries;
    };

    const [changeDefaultPaymentMethod] = useChangeDefaultPaymentMethodMutation();

    const handleDefaultPaymentMethodChange = async (
        attendeeId: string,
        paymentMethodId: string,
        selectedPaymentMethod: PaymentMethod,
    ) => {
        try {
            message.loading({
                content: i18n.views.PaymentMethodTable.changingDefaultPaymentMethod(),
                duration: 0,
                key: `${attendeeId}-${paymentMethodId}`,
            });
            await changeDefaultPaymentMethod({
                variables: {
                    attendeeId,
                    paymentMethodId,
                },
                refetchQueries: refetchQueries(),
            }).then((res) => {
                console.log("res", res);

                const newDefaultPaymentMethod = res.data?.changeDefaultPaymentMethod.defaultPaymentMethod;

                if (newDefaultPaymentMethod) {
                    const paymenMethod = getPaymentMethodType(newDefaultPaymentMethod) as keyof PossibleTranslations;

                    const translatedPaymentMethod = i18n.views.PaymentMethodBadge[paymenMethod]();

                    setSelectedRowKeys([paymentMethodId]);
                    setSelectedPayment(selectedPaymentMethod);
                    setChangeSectionOpen(false);

                    message.success({
                        content: `${i18n.views.PaymentMethodTable.defaultPaymentMethodChanged()} ${translatedPaymentMethod}`,
                        duration: 3,
                        key: `${attendeeId}-${paymentMethodId}`,
                    });
                }
            });
        } catch (error) {
            console.error("Error changing default payment method", error);
            console.log("error", error);
            message.error({
                content: i18n.views.PaymentMethodTable.errorChangingDefaultPaymentMethod(),
                duration: 5,
                key: `${attendeeId}-${paymentMethodId}`,
            });
        }
    };

    useEffect(() => {
        if (bookerPaymentMethodsData?.getPaymentMethodsByBookerId) {
            const allPaymentMethods = bookerPaymentMethodsData.getPaymentMethodsByBookerId;

            // Make sure that ESR is only selectable if company has ESR. Die aktuell
            // gewählte Methode der Buchung bleibt trotzdem sichtbar, sonst zeigt der
            // Kompaktmodus eine gespeicherte ESR-Methode als „keine Auswahl“.
            const selectablePaymentMethods = allPaymentMethods.filter((paymentMethod) => {
                return !paymentMethod.esr || companyPaymentMethods?.esr || paymentMethod.id === paymentMethodId;
            });

            setPaymentMethodsOfBooker(selectablePaymentMethods);
        }
    }, [bookerPaymentMethodsData, companyPaymentMethods, paymentMethodId]);

    // ------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        if (paymentMethodId) {
            setSelectedRowKeys([paymentMethodId]);
        }
    }, [paymentMethodId]);

    // Phase 3 (P3-3) — Default-Select je Mollie-Modus (Entscheidung D):
    //   - "mandatory"/"optional": Online wird nur vorausgewählt, wenn KEINE gültige
    //     Vorauswahl existiert (Create-Mount: Caller übergibt bei mandatory undefined).
    //     Eine gültige EIGENE Methode wird NICHT überschrieben — auf der Edit-Seite
    //     ist das die gespeicherte Methode der Buchung; das frühere mandatory-Force
    //     zeigte dort Online statt DB-Wahrheit und hätte beim Speichern die Methode
    //     still auf Online umgeschrieben (B-1515). Auch eine explizite Admin-Wahl
    //     überlebt so einen Remount (Step-Wechsel im Create-Wizard).
    //   - "off": kein Effekt (Caller setzt Default).
    //   - mollieDefaultForced: tracking-Flag — wir forcen nur einmal beim Mount,
    //     sonst kann der Admin nicht aktiv auf "Bar" o.ä. wechseln.
    const selectOnlinePaymentMethod = () => {
        const existingOnline = paymentMethodsOfBooker.find((pm) => pm.onlinePayment === true);
        if (existingOnline && existingOnline.id) {
            setSelectedRowKeys([existingOnline.id]);
            setSelectedPayment(existingOnline);
        } else {
            setSelectedRowKeys([AUTO_CREATE_ONLINE_PAYMENT_METHOD_MARKER]);
            setSelectedPayment({
                id: AUTO_CREATE_ONLINE_PAYMENT_METHOD_MARKER,
                onlinePayment: true,
                bookerId,
            } as PaymentMethod);
        }
    };
    const [mollieDefaultForced, setMollieDefaultForced] = useState(false);
    useEffect(() => {
        if (!mollieEnabled || mollieDefaultForced) return;
        const currentIsOnline =
            paymentMethodId === AUTO_CREATE_ONLINE_PAYMENT_METHOD_MARKER
            || paymentMethodsOfBooker.find((pm) => pm.id === paymentMethodId)?.onlinePayment === true;
        if (currentIsOnline) {
            setMollieDefaultForced(true);
            return;
        }
        // Gültige Vorauswahl einer EIGENEN Methode respektieren (nicht auf Online
        // forcieren). Gültig = referenziert eine vorhandene Zeile.
        const currentIsValidOwnMethod =
            paymentMethodId !== null
            && paymentMethodId !== undefined
            && paymentMethodsOfBooker.some((pm) => pm.id === paymentMethodId);
        if (currentIsValidOwnMethod) {
            setMollieDefaultForced(true);
            return;
        }
        // Keine gültige Vorauswahl: Online vorauswählen.
        selectOnlinePaymentMethod();
        setMollieDefaultForced(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mollieEnabled, isMollieOptional, paymentMethodsOfBooker, paymentMethodId, mollieDefaultForced]);

    // Mitarbeiter-Sperre (ADR 0013) — Ausweich-Vorauswahl ----------------------------------------------------------
    // Kandidaten für die Ausweich-Vorauswahl: eigene (nicht-Online-) Methoden des
    // Buchers, die der Server auch akzeptiert (B-82808: leere Lastschrift fällt raus).
    const ownMethodRows = useMemo(
        () =>
            (paymentMethodsOfBooker as Array<PaymentMethodRow>).filter(
                (pm) => pm.onlinePayment !== true && isPaymentMethodUsableForBooking(pm),
            ),
        [paymentMethodsOfBooker],
    );
    const firstEnabledOwnMethod = useMemo(
        () => pickFirstEnabledOwnMethod(ownMethodRows, staffRestriction.enabledOwnMethods),
        [ownMethodRows, staffRestriction.enabledOwnMethods],
    );

    // Einmalig beim Mount: Ist die vom Aufrufer übergebene Vorauswahl (Teilnehmer-
    // Standard, beim Umwandeln einer Platzreservierung deren Methode) für Mitarbeiter
    // gesperrt und keine Bestandsmethode, weicht die Tabelle aus: im Mollie-Kurs auf
    // Online (immer freigegeben, wie `resolveDefaultPaymentMethod` im Optional-Modus),
    // sonst auf die erste freigegebene Methode des Buchers (SEPA → Überweisung → Bar
    // → ESR). Gibt es keine, bleibt die Vorauswahl stehen (ADR 0013: automatische
    // Pfade brechen nie ab, letzte Stufe = heutiges Verhalten) und ein Hinweis fordert
    // zum Anlegen einer freigegebenen Methode auf. Bewusst hier und nicht in den
    // Step-Komponenten: Standard- und Einzelbuchung laufen so garantiert identisch
    // (Buchungsarten-Parität). Nur einmal forcieren, damit danach frei gewechselt wird.
    const [staffFallbackApplied, setStaffFallbackApplied] = useState(false);
    useEffect(() => {
        if (staffFallbackApplied || staffRestriction.loading) return;
        // Kein Schalter oder keine Auswahl-UI ⇒ nichts zu tun.
        if (!staffRestriction.restrictStaff || !interactive) {
            setStaffFallbackApplied(true);
            return;
        }
        // Keine Vorauswahl oder Online-Sentinel ⇒ die Sperre greift nicht.
        if (!paymentMethodId || paymentMethodId === AUTO_CREATE_ONLINE_PAYMENT_METHOD_MARKER) return;
        const preselected = (paymentMethodsOfBooker as Array<PaymentMethodRow>).find((pm) => pm.id === paymentMethodId);
        // Zeile (noch) nicht geladen ⇒ später erneut prüfen.
        if (!preselected) return;
        setStaffFallbackApplied(true);
        // Freigegeben oder Bestandsmethode der Buchung ⇒ bleibt.
        if (!isStaffBlocked(preselected)) return;
        if (mollieEnabled) {
            selectOnlinePaymentMethod();
        } else if (firstEnabledOwnMethod && firstEnabledOwnMethod.id) {
            setSelectedRowKeys([firstEnabledOwnMethod.id]);
            setSelectedPayment(firstEnabledOwnMethod);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [staffFallbackApplied, staffRestriction, interactive, storedPaymentMethodId, mollieEnabled, paymentMethodId, paymentMethodsOfBooker, firstEnabledOwnMethod]);

    const renderBadge = (paymentMethod: PaymentMethodData) => {
        const paymentMethodType = getPaymentMethodType(paymentMethod);
        const paymentProvider = getProviderForPaymentMethodType(paymentMethodType, companySettings);

        return <PaymentMethodBadge type={paymentMethodType} paymentProvider={paymentProvider} />;
    };

    // Hint if is not company method -------------------------------------------------------------------------------
    const isCompanyMethod = (record: PaymentMethodData) => {
        if (!companyPaymentMethods) return null;
        if (record.esr && companyPaymentMethods.esr) return null;
        if (record.bankTransfer && companyPaymentMethods.bankTransfer) return null;
        if (record.cash && companyPaymentMethods.cash) return null;

        if (record.iban !== null && companyPaymentMethods.sepaDebit) return null;

        // Mit Mitarbeiter-Sperre (ADR 0013) ist die Methode nicht nur ein Hinweis,
        // sondern tatsächlich gesperrt — dann der passende Text am Warn-Icon.
        const title = isStaffBlocked(record as PaymentMethodRow)
            ? i18n.views.PaymentMethodTable.restrictedMethod()
            : i18n.views.PaymentMethodTable.notCompanyMethod();

        return (
            <Tooltip title={title}>
                <ExclamationCircleOutlined style={{color: red[3], fontSize: "1.5em"}} />
            </Tooltip>
        );
    };

    // columns -----------------------------------------------------------------------------------------------------
    const columns: Array<ColumnProps<PaymentMethodData>> = [
        {
            title: "",
            dataIndex: "companyPaymentMethod",
            render: (text, record: PaymentMethodData) => {
                return isCompanyMethod(record);
            },
        },
        {
            title: i18n.views.PaymentMethodTable.method(),
            dataIndex: "id",
            render: (text, record: PaymentMethodData) => {
                const badge = renderBadge(record);
                // Bei der Online-Zahlung (Mollie) erläutern, welche Methoden der
                // Kunde im Checkout zur Auswahl hat (Kreditkarte/PayPal).
                const content =
                    record.onlinePayment === true ? (
                        <span>
                            {badge}
                            <div style={{fontSize: 12, color: "rgba(0,0,0,.45)", marginTop: 2}}>
                                {i18n.views.PaymentMethodTable.onlinePaymentMethodsHint()}
                            </div>
                        </span>
                    ) : (
                        badge
                    );
                // Phase 12 / LIFE-03 (D-10): Tooltip auf der Online-Zahlung-Zeile,
                // wenn die Buchung eine bezahlte Mollie-SP enthält — Admin sieht
                // den Grund für das Read-Only-Verhalten.
                if (hasLockedMollieSPInBooking && record.onlinePayment === true) {
                    return (
                        <Tooltip title={i18n.views.PaymentMethodTable.mollieRefundRequiredTooltip()}>
                            {content}
                        </Tooltip>
                    );
                }
                // ADR 0013 — Mitarbeiter-Sperre: Zeile bleibt sichtbar, ist aber nicht
                // wählbar. Tooltip nennt den Grund und den Ausweg.
                if (isStaffBlocked(record as PaymentMethodRow)) {
                    return (
                        <Tooltip title={i18n.views.PaymentMethodTable.restrictedMethod()}>
                            <span>{content}</span>
                        </Tooltip>
                    );
                }
                // B-82808: leere Lastschrift-Methode (Kontoinhaber/IBAN fehlen) — die Zeile ist
                // nicht wählbar (Server wirft PAYMENT_METHOD_INCOMPLETE), hier der Grund dazu.
                if (!isPaymentMethodUsableForBooking(record)) {
                    // Der Badge ist eine memo()-Komponente ohne DOM-Prop-Forwarding — der
                    // Tooltip braucht ein echtes Element als Trigger.
                    return (
                        <Tooltip title={i18n.views.PaymentMethodTable.incompleteSepaDebitTooltip()}>
                            <span>{content}</span>
                        </Tooltip>
                    );
                }
                return content;
            },
        },
        {
            title: i18n.views.PaymentMethodTable.sepaAccountholder(),
            dataIndex: "sepaAccountholder",
        },
        {
            title: i18n.views.PaymentMethodTable.iban(),
            dataIndex: "iban",
        },
        {
            title: i18n.views.PaymentMethodTable.bank(),
            dataIndex: "bank",
        },
    ];

    const onSelectChange = (selectedRowKeys: Array<string> | Array<number>, selectedRows: Array<PaymentMethod>) => {
        const selectedPaymentMethod = selectedRows[0];
        const selectedRowKey = selectedRowKeys[0].toString();
        const attendeeId = attendee?.id;

        // Phase „Online optional" — Crash-Fix (Stage): Die injizierte Online-Sentinel-
        // Zeile (`AUTO_CREATE_ONLINE_PAYMENT_METHOD_MARKER`) ist KEINE persistierte
        // PaymentMethod. Sie darf NIE in den Attendee-Default-Change-Pfad laufen, sonst
        // schickt `changeDefaultPaymentMethod` den Marker als paymentMethodId an den
        // Server, der ihn gegen die uuid-Spalte abfragt → Crash/BadRequest. Der Sentinel
        // wird erst beim createBooking serverseitig (resolveOrCreateOnlinePaymentMethod)
        // zu einer echten PaymentMethod aufgelöst. Daher: nur LOKAL selektieren, KEIN
        // Default-Change-Modal, KEINE Mutation.
        const isOnlineSentinel = selectedRowKey === AUTO_CREATE_ONLINE_PAYMENT_METHOD_MARKER;

        // ADR 0013 (#1033): Eine gesperrte Methode ist nur als Bestandsmethode der Buchung
        // wählbar (alle anderen gesperrten Zeilen sind disabled). Sie gilt dann nur für
        // diese Buchung und wird nie Teilnehmer-Standard — kein Dialog, keine Mutation.
        const changesAttendeeDefault =
            !isOnlineSentinel
            && selectedRowKey !== defaultPaymentMethodId
            && !isStaffRestrictedType(selectedPaymentMethod);

        // Gemeinsamer "Apply"-Handler, der nach allen Modals ausgeführt wird
        // (sowohl Default-Change-Path als auch direkte-Selektion-Path).
        // „Zahlmethode ändern“ schließt erst, wenn die Auswahl wirklich übernommen
        // ist (im Default-Change-Pfad also nach erfolgreicher Mutation).
        const applySelection = () => {
            if (attendeeId && changesAttendeeDefault) {
                handleDefaultPaymentMethodChange(attendeeId, selectedRowKey, selectedPaymentMethod);
            } else {
                setSelectedRowKeys(selectedRowKeys);
                setSelectedPayment(selectedRows[0]);
                setChangeSectionOpen(false);
            }
        };

        // Phase 12 / LIFE-04 (D-11): Warn-Modal VOR Default-Confirm/Selektion,
        // wenn eine offene Mollie-SP in der Buchung existiert UND der Admin wechselt
        // weg von einer Online-Methode. Der Wechsel invalidiert den Zahlungslink.
        const triggerWarnIfNeeded = (onConfirm: () => void) => {
            if (hasOpenMollieSPInBooking && !selectedPaymentMethod?.onlinePayment) {
                Modal.confirm({
                    title: i18n.views.PaymentMethodTable.molliePaymentUrlInvalidTitle(),
                    content: i18n.views.PaymentMethodTable.molliePaymentUrlInvalidContent(),
                    okText: i18n.views.PaymentMethodTable.molliePaymentUrlInvalidOk(),
                    cancelText: i18n.views.PaymentMethodTable.molliePaymentUrlInvalidCancel(),
                    icon: <ExclamationCircleOutlined />,
                    onOk: onConfirm,
                });
            } else {
                onConfirm();
            }
        };

        // Phase 12 / D-04 + B2: Info-Modal bei Attendee-Default-Change auf non-Online-
        // Methode. Erklärt Scope-Begrenzung ("existierende Buchungen bleiben unverändert").
        // NICHT-blockierend — keine Backend-Guard-Ergänzung, rein informativ.
        const triggerAttendeeDefaultInfoIfNeeded = (onConfirm: () => void) => {
            const isSwitchingAwayFromOnline = !selectedPaymentMethod?.onlinePayment;
            if (attendeeId && selectedRowKey !== defaultPaymentMethodId && isSwitchingAwayFromOnline) {
                Modal.info({
                    title: i18n.views.PaymentMethodTable.attendeeDefaultChangeInfoTitle(),
                    content: i18n.views.PaymentMethodTable.attendeeDefaultChangeInfoContent(),
                    okText: "OK",
                    onOk: onConfirm,
                });
            } else {
                onConfirm();
            }
        };

        if (attendeeId && changesAttendeeDefault) {
            // Sequenz: Warn-Modal (D-11, wenn offene Mollie-SP) → Info-Modal (D-04)
            //          → Default-Change-Confirm (bestehend) → Mutation.
            triggerWarnIfNeeded(() => {
                triggerAttendeeDefaultInfoIfNeeded(() => {
                    Modal.confirm({
                        title: i18n.views.PaymentMethodTable.confirmChangeDefaultPaymentMethodTitle(),
                        content: (
                            <>
                                <p>{i18n.views.PaymentMethodTable.confirmChangeDefaultPaymentMethod()}</p>
                                <p>{i18n.views.PaymentMethodTable.confirmChangeDefaultPaymentMethodDescription()}</p>
                            </>
                        ),
                        icon: null,
                        onOk: async () => applySelection(),
                        style: {textAlign: "center"},
                    });
                });
            });
        } else {
            // Kein Attendee-Default-Change (d.h. normaler Booking-Methoden-Switch).
            // D-11 Warn-Modal gilt auch hier: wenn offene Mollie-SP vorhanden und
            // Admin wechselt weg von Online → Warn.
            triggerWarnIfNeeded(() => applySelection());
        }
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        // Phase 12 / LIFE-03 (D-10): Alle non-Online-Radio-Zeilen werden disabled,
        // wenn die Buchung eine bezahlte Mollie-SP enthält — strengere Variante aus
        // Plan 12-02 Task 1d (Admin kann NICHT weg von Online-Payment ohne Refund).
        // Die Online-Zahlung-Zeile selbst bleibt aktiv/auswählbar (keine Sperre).
        // Cast zu `PaymentMethod`, weil `onChange` den Row-Typ auf `PaymentMethod`
        // festpinnt (bestehende Baseline-Typisierung; `PaymentMethodData` ist ein
        // strenger `Pick`, der dieselben Felder exponiert).
        // B-82808: zusätzlich sind leere Lastschrift-Zeilen (Typ SepaDebit, aber Kontoinhaber
        // oder IBAN fehlt) nicht wählbar — der Server lehnt sie beim Speichern ab und im
        // Buchungs-Wizard ginge dabei der Wizard-Zustand verloren. Eine bereits gespeicherte
        // Altfall-Auswahl wird NICHT umgesetzt, nur deaktiviert dargestellt.
        // ADR 0013: Mit aktivem Tenant-Schalter sind Zeilen nicht freigegebener Arten
        // gesperrt — bis auf die Bestandsmethode der bearbeiteten Buchung.
        getCheckboxProps: (record: PaymentMethod) => ({
            disabled:
                (hasLockedMollieSPInBooking && record.onlinePayment !== true)
                || !isPaymentMethodUsableForBooking(record)
                || isStaffBlocked(record),
        }),
    };

    // Phase 3 (P3-3) — Pseudo-Eintrag "Online-Zahlung" wird in BEIDEN Mollie-Modi
    // (mandatory UND optional) injiziert, sofern der Bucher keine eigene Online-
    // Methode hat. Im optionalen Modus bleiben die eigenen Methoden zusätzlich
    // sichtbar (kombinierte Tabelle). Sentinel-ID wird beim createBooking
    // serverseitig zu einer echten PaymentMethod-ID aufgelöst.
    const displayedPaymentMethods = useMemo(() => {
        if (!mollieEnabled) return paymentMethodsOfBooker;
        const hasExistingOnline = paymentMethodsOfBooker.some(
            (pm) => pm.onlinePayment === true,
        );
        if (hasExistingOnline) return paymentMethodsOfBooker;
        const pseudoOnline: Partial<PaymentMethod> = {
            id: AUTO_CREATE_ONLINE_PAYMENT_METHOD_MARKER,
            onlinePayment: true,
            bookerId,
        };
        return [pseudoOnline, ...paymentMethodsOfBooker];
    }, [mollieEnabled, paymentMethodsOfBooker, bookerId]);

    const activeKey = selectedRowKeys.length > 0 ? String(selectedRowKeys[0]) : undefined;
    const activePaymentMethods = useMemo(
        () => displayedPaymentMethods.filter((pm) => pm.id === activeKey),
        [displayedPaymentMethods, activeKey],
    );
    // Der Online-Sentinel ist keine vorhandene Methode des Buchers (wird erst beim
    // Speichern angelegt) und gehört deshalb in die Gruppe „Neue Bezahlmethode anlegen“.
    const otherPaymentMethods = useMemo(
        () =>
            displayedPaymentMethods.filter(
                (pm) => pm.id !== activeKey && pm.id !== AUTO_CREATE_ONLINE_PAYMENT_METHOD_MARKER,
            ),
        [displayedPaymentMethods, activeKey],
    );
    const pendingOnlinePaymentMethods = useMemo(
        () =>
            displayedPaymentMethods.filter(
                (pm) => pm.id !== activeKey && pm.id === AUTO_CREATE_ONLINE_PAYMENT_METHOD_MARKER,
            ),
        [displayedPaymentMethods, activeKey],
    );

    const renderTable = (dataSource: Array<Partial<PaymentMethod>>, keys: Array<string> | Array<number>) => (
        <Table
            rowKey="id"
            showHeader={showHeader}
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            size="small"
            rowSelection={
                interactive
                    ? {
                          type: "radio",
                          ...rowSelection,
                          selectedRowKeys: keys,
                      }
                    : undefined
            }
            bordered={false}
        />
    );

    // ADR 0013: Der Bucher hat keine Methode einer freigegebenen Art und es ist
    // nichts oder eine gesperrte Methode ausgewählt ⇒ Hinweis statt stiller Auswahl.
    // Im Mollie-Kurs entfällt er, dort ist Online immer wählbar.
    const activeRowIsStaffBlocked = activePaymentMethods.length > 0 && isStaffBlocked(activePaymentMethods[0] as PaymentMethodRow);
    const noEnabledMethodHint =
        interactive
        && staffRestriction.restrictStaff
        && !staffRestriction.loading
        && !mollieEnabled
        && !firstEnabledOwnMethod
        && (selectedRowKeys.length === 0 || activeRowIsStaffBlocked) ? (
            <Alert
                type={activeRowIsStaffBlocked ? "warning" : "info"}
                showIcon
                style={{marginTop: 10}}
                data-testid="payment-method-no-enabled-method"
                message={
                    activeRowIsStaffBlocked
                        ? i18n.views.PaymentMethodTable.blockedMethodKept()
                        : i18n.views.PaymentMethodTable.noEnabledPaymentMethod()
                }
            />
        ) : null;

    if (!activeOnly) {
        return (
            <div className={css.root}>
                {renderTable(displayedPaymentMethods, selectedRowKeys)}
                {noEnabledMethodHint}
            </div>
        );
    }

    const canAddPaymentMethods = Boolean(bookerId && courseId);
    const showNewSection = canAddPaymentMethods || pendingOnlinePaymentMethods.length > 0;
    const noAlternatives = otherPaymentMethods.length === 0 && !showNewSection;

    return (
        <div className={css.root}>
            {activePaymentMethods.length > 0 ? (
                renderTable(activePaymentMethods, selectedRowKeys)
            ) : (
                <div className={css.noSelection} data-testid="payment-method-none-selected">
                    {i18n.views.PaymentMethodTable.noPaymentMethodSelected()}
                </div>
            )}
            {noEnabledMethodHint}
            {interactive && !noAlternatives && (
                <Collapse
                    ghost
                    className={css.changeSection}
                    activeKey={changeSectionOpen ? ["change"] : []}
                    onChange={(keys) => setChangeSectionOpen(keys.length > 0)}
                >
                    <Collapse.Panel key="change" header={i18n.views.PaymentMethodTable.changePaymentMethod()}>
                        {otherPaymentMethods.length > 0 && (
                            <>
                                <Divider orientation="left" plain className={css.groupDivider}>
                                    {i18n.views.PaymentMethodTable.bookerPaymentMethods()}
                                </Divider>
                                {renderTable(otherPaymentMethods, [])}
                            </>
                        )}
                        {showNewSection && (
                            <>
                                <Divider orientation="left" plain className={css.groupDivider}>
                                    {i18n.views.PaymentMethodTable.newPaymentMethods()}
                                </Divider>
                                {pendingOnlinePaymentMethods.length > 0 && renderTable(pendingOnlinePaymentMethods, [])}
                                {canAddPaymentMethods && (
                                    <BookingAddPaymentMethod
                                        bookerId={bookerId}
                                        courseId={courseId}
                                        bookingId={bookingId}
                                        setSelectedPayment={(pm: PaymentMethod) => {
                                            setSelectedRowKeys([pm.id]);
                                            setSelectedPayment(pm);
                                        }}
                                        setModalOpen={(open: boolean) => setChangeSectionOpen(open)}
                                        okButtonText={i18n.views.PaymentMethodTable.addPaymentMethod()}
                                    />
                                )}
                            </>
                        )}
                    </Collapse.Panel>
                </Collapse>
            )}
        </div>
    );
};

export default PaymentMethodTable;
